import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import {
  fetchAdminListingDuplicatesQueue,
  fetchAdminListingMissingPhotosQueue,
  fetchAdminListingOutliersQueue,
  fetchAdminListingReviewQueue,
  fetchAdminListingStaleQueue,
  runAdminListingAction,
  flagAdminListingDuplicate,
} from "../../../services/automapDealerInventoryApi";
import { formatListingStatus, qualityBadgeClass } from "../../../constants/automapDealerInventoryConstants";
import {
  DUPLICATE_STATUS_LABELS,
  OUTLIER_REASON_LABELS,
} from "../../../constants/automapAdminListingConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  AdminListingNav,
  ConfirmDialog,
  formatCop,
  formatDate,
  queueFromPathname,
  trackListingAdminEvent,
  vehicleSummary,
} from "./listingAdminShared";

const QUEUE_META = {
  review: {
    title: "Cola de revisión",
    description: "Listings que requieren revisión antes de publicación.",
    fetch: fetchAdminListingReviewQueue,
    event: "admin_review_queue_viewed",
  },
  stale: {
    title: "Inventario obsoleto",
    description: "Listings activos sin confirmación reciente o con disponibilidad pendiente.",
    fetch: fetchAdminListingStaleQueue,
    event: "admin_stale_queue_viewed",
  },
  "missing-photos": {
    title: "Sin fotos",
    description: "Listings sin fotos o con cobertura insuficiente.",
    fetch: fetchAdminListingMissingPhotosQueue,
    event: "admin_missing_photos_queue_viewed",
  },
  duplicates: {
    title: "Duplicados",
    description: "Candidatos a duplicado según señales de importación y coincidencias.",
    fetch: fetchAdminListingDuplicatesQueue,
    event: "admin_duplicates_queue_viewed",
  },
  outliers: {
    title: "Precio / kilometraje atípico",
    description: "Listings con valores sospechosos según reglas básicas.",
    fetch: fetchAdminListingOutliersQueue,
    event: "admin_outliers_queue_viewed",
  },
};

function AdminListingQueuePageContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const queue = queueFromPathname(location.pathname);
  const meta = QUEUE_META[queue];
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async () => {
    if (!meta) return;
    setLoading(true);
    setError("");
    try {
      const data = await meta.fetch({});
      setListings(data.listings || []);
      trackListingAdminEvent(meta.event);
    } catch (err) {
      setError(err.message || "No se pudo cargar la cola.");
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [queue]);

  const runAction = async (listingId, action, extra = {}) => {
    try {
      await runAdminListingAction(listingId, action, extra);
      trackListingAdminEvent(`admin_listing_${action}`, { listingId });
      await load();
    } catch (err) {
      setError(err.message || "Acción fallida.");
    }
  };

  const markDuplicate = async (listingId, duplicateOfListingId) => {
    try {
      await flagAdminListingDuplicate(listingId, {
        duplicateOfListingId,
        duplicateStatus: "likely_duplicate",
      });
      await load();
    } catch (err) {
      setError(err.message || "No se pudo marcar duplicado.");
    }
  };

  if (!meta) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message="Cola no reconocida." />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout className="admin-listings-page">
      <AdminAutomotiveNav active="listings" />
      <AdminPageHeader
        eyebrow="AutoMapa · Inventario"
        title={meta.title}
        description={meta.description}
        navigation={<AdminListingNav active={queue} />}
      />
      <main className="admin-console__main">
        {error ? <AdminStatePanel variant="empty" message={error} onRetry={load} retryLabel="Reintentar" /> : null}
        {loading ? (
          <AdminStatePanel variant="empty" message="Cargando cola…" />
        ) : listings.length === 0 ? (
          <AdminStatePanel variant="empty" message="No hay listings en esta cola." />
        ) : (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Vehículo</th>
                  <th>Dealer</th>
                  {queue === "stale" ? <th>Días obsoleto</th> : null}
                  {queue === "stale" ? <th>WhatsApp</th> : null}
                  {queue === "duplicates" ? <th>Coincidencias</th> : null}
                  {queue === "outliers" ? <th>Motivos</th> : null}
                  <th>Estado</th>
                  <th>Disponibilidad</th>
                  <th>Calidad</th>
                  <th>Confirmado</th>
                  <th>Fotos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => (
                  <tr key={listing.id}>
                    <td>
                      <Link to={`/admin/listings/${listing.id}`}>{vehicleSummary(listing)}</Link>
                      <div className="muted">{formatCop(listing.priceCop)}</div>
                    </td>
                    <td>
                      <Link to={`/admin/dealers/${listing.dealerId}`}>{listing.dealerName || listing.dealerId}</Link>
                    </td>
                    {queue === "stale" ? <td>{listing.daysStale ?? "—"}</td> : null}
                    {queue === "stale" ? (
                      <td>
                        {listing.dealerWhatsapp ? (
                          <a href={`https://wa.me/${listing.dealerWhatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                            Contactar
                          </a>
                        ) : (
                          <span title="TODO: notificación automática">—</span>
                        )}
                      </td>
                    ) : null}
                    {queue === "duplicates" ? (
                      <td>
                        {(listing.duplicateMatches || []).slice(0, 2).map((match) => (
                          <div key={match.listingId}>
                            <Link to={`/admin/listings/${match.listingId}`}>
                              {DUPLICATE_STATUS_LABELS[match.type] || match.type}
                            </Link>
                          </div>
                        ))}
                      </td>
                    ) : null}
                    {queue === "outliers" ? (
                      <td>
                        {(listing.outlierReasons || []).map((reason) => (
                          <span key={reason} className="admin-badge admin-badge--warning" style={{ marginRight: 4 }}>
                            {OUTLIER_REASON_LABELS[reason] || reason}
                          </span>
                        ))}
                      </td>
                    ) : null}
                    <td>{formatListingStatus(listing.status)}</td>
                    <td>{formatListingStatus(listing.availabilityStatus)}</td>
                    <td>
                      <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>{listing.qualityScore ?? "—"}</span>
                    </td>
                    <td>{formatDate(listing.lastConfirmedAt)}</td>
                    <td>{listing.photoCount ?? 0}</td>
                    <td className="dealer-inventory-actions">
                      <Link to={`/admin/listings/${listing.id}`}>Ver</Link>
                      <Link to={`/admin/listings/${listing.id}/edit`}>Editar</Link>
                      {canManage ? (
                        <>
                          {queue === "review" ? (
                            <button type="button" onClick={() => runAction(listing.id, "approve")}>Aprobar</button>
                          ) : null}
                          {queue === "stale" ? (
                            <>
                              <button type="button" onClick={() => runAction(listing.id, "confirm_available")}>Confirmar</button>
                              <button type="button" onClick={() => runAction(listing.id, "pending_confirmation")}>Pendiente</button>
                            </>
                          ) : null}
                          {queue === "missing-photos" ? (
                            <button type="button" onClick={() => runAction(listing.id, "needs_review")}>Revisión</button>
                          ) : null}
                          {queue === "duplicates" && listing.duplicateMatches?.[0] ? (
                            <button
                              type="button"
                              onClick={() => markDuplicate(listing.id, listing.duplicateMatches[0].listingId)}
                            >
                              Marcar dup.
                            </button>
                          ) : null}
                          <button type="button" onClick={() => setConfirmAction({ listingId: listing.id, action: "pause" })}>Pausar</button>
                          {queue === "review" || queue === "outliers" ? (
                            <button type="button" onClick={() => navigate(`/admin/listings/${listing.id}/edit`)}>Corregir</button>
                          ) : null}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </main>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title="Confirmar acción"
        message={`¿Pausar listing ${confirmAction?.listingId}?`}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          runAction(confirmAction.listingId, confirmAction.action);
          setConfirmAction(null);
        }}
      />
    </AdminPageLayout>
  );
}

export default function AdminListingQueuePage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminListingQueuePageContent />
    </RequirePermission>
  );
}
